import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useAuth } from 'react-oidc-context'
import { AddOutlined, WarningAmberOutlined } from '@mui/icons-material'
import { Alert, Box, Grid, Skeleton, Stack, Typography } from '@mui/material'
import { useSnackbar } from 'notistack'

import ButtonLink from '../../../../components/button-link'
import { AppContext, useHost } from '../../../../providers/app-provider'
import Api from '../../../../utils/api'
import PeopleContext from '../../context'
import useStyles from '../../index.styles'

import TransferList from './transfer-list'

const SubscriptionsContent = ({ isEditing, setIsEditing }: { isEditing: boolean; setIsEditing: React.Dispatch<React.SetStateAction<boolean>> }) => {
  const classes = useStyles
  const { enqueueSnackbar } = useSnackbar()

  const { user } = useContext(PeopleContext)
  const { pageRefreshCounter } = useContext(AppContext)
  const auth = useAuth()
  const host = useHost()

  const [subscriptions, setSubscriptions] = useState<ISubscriptionListItem[]>([])
  const [availableSchemas, setAvailableSchemas] = useState<ISubscriptionListItem[]>([])
  const [loading, setLoading] = useState<boolean>(true)

  const userDisabledPreferences = useMemo(
    () => ['push', 'sms', 'email', 'phone'].filter((x) => !(user?.preferences?.notification ?? []).includes(x)),
    [user?.preferences?.notification]
  )

  const fetchSubscriptions = useCallback(async () => {
    if (!host || !auth.user?.access_token) return
    if (user) {
      setLoading(true)
      Promise.all([Api.get(`${host}/api/notificationschemas`), Api.get(`${host}/api/reactions`), Api.get(`${host}/api/usersubscriptions?userId=${user.id}`)])
        .then(([notificationRes, reactionsRes, subscriptionRes]) => {
          if (notificationRes.ok && reactionsRes.ok && subscriptionRes.ok) {
            // convert the list of objects into a map [id] => object
            const notificationSchemas: Record<number, INotificationSchemaType> = notificationRes.body.reduce(
              (combinedMap: Record<number, INotificationSchemaType>, nextItem: INotificationSchemaType) => ({
                ...combinedMap,
                [nextItem.id]: nextItem,
              }),
              {}
            )
            const userSubscriptions: ISubscriptionType[] = subscriptionRes.body
            const subs = userSubscriptions.map((us) => {
              const notificationSchema = notificationSchemas[us.notificationSchemaId]
              const reactions = notificationSchema?.reactions.map((ns) => reactionsRes.body.find((r: IReactionType) => r.id === ns))
              // isDisabled is true if any of the reactions has a type that is in userDisabledPreferences
              const isDisabled = reactions?.some((r) => userDisabledPreferences.includes(r?.type))

              return {
                id: us.notificationSchemaId,
                name: notificationSchema?.name ?? `Id: ${us.notificationSchemaId}`,
                disabled: isDisabled,
              }
            })
            subs.sort((a, b) => a.name.localeCompare(b.name))

            setSubscriptions(subs)
            setAvailableSchemas(
              notificationRes.body
                .map((ns: INotificationSchemaType) => ({ id: ns.id, name: ns.name, disabled: false }))
                .sort((a: ISubscriptionListItem, b: ISubscriptionListItem) => a.name.localeCompare(b.name))
            )
          }
          if (!notificationRes.ok) {
            enqueueSnackbar('Something went wrong. Notification schemas did not load', { variant: 'error' })
          }
          if (!reactionsRes.ok) {
            enqueueSnackbar('Something went wrong. Reactions did not load', { variant: 'error' })
          }
          if (!subscriptionRes.ok) {
            enqueueSnackbar('Something went wrong. User subscriptions did not load', { variant: 'error' })
          }
          setLoading(false)
        })
        .finally(() => setLoading(false))
    }
  }, [auth.user?.access_token, enqueueSnackbar, host, user, userDisabledPreferences])

  const saveSubscriptions = async (newList: ISubscriptionListItem[]) => {
    if (!host || !auth.user?.access_token) return
    if (user) {
      // Compare new list to user's subscriptions and create new subscriptions, delete subscriptions that are not in the new list
      const subscriptionsToRemove = subscriptions.filter((s) => !newList.find((ns) => ns.id === s.id))
      const subscriptionsToCreate = newList.filter((s) => !subscriptions.find((ns) => ns.id === s.id))

      let hadRemovalErrors = false
      let hadAddingErrors = false

      // Remove subscriptions if any
      if (subscriptionsToRemove.length > 0) {
        await Promise.all(subscriptionsToRemove.map((s) => Api.del(`${host}/api/usersubscriptions?notificationSchemaId=${s.id}&userId=${user.id}`))).then(
          (responses: { ok: boolean }[]) => {
            // If any or some responses are not ok, give an error snackbar
            if (responses.every((r) => !r.ok)) {
              hadRemovalErrors = true
              enqueueSnackbar('Something went wrong, and subscriptions could not be removed', { variant: 'error' })
            } else if (responses.some((r) => !r.ok)) {
              hadRemovalErrors = true
              enqueueSnackbar('Something went wrong, and some subscriptions could not be removed', { variant: 'error' })
            }
          }
        )
      }
      // Create subscriptions if any
      if (subscriptionsToCreate.length > 0) {
        const res = await Api.post(`${host}/api/usersubscriptions/batch`, {
          notificationSchemaIds: subscriptionsToCreate.map((s) => s.id),
          userId: user.id,
        })
        if (!res.ok) {
          hadAddingErrors = true
          enqueueSnackbar('Something went wrong, and subscriptions could not be added', { variant: 'error' })
        }
      }
      if (!hadRemovalErrors && !hadAddingErrors) {
        setIsEditing(false)
        enqueueSnackbar('Subscriptions updated successfully')
      } else if (!hadRemovalErrors && hadAddingErrors) {
        enqueueSnackbar('Subscriptions removed successfully')
      } else if (hadRemovalErrors && !hadAddingErrors) {
        enqueueSnackbar('Subscriptions added successfully')
      }
      await fetchSubscriptions()
    }
  }

  useEffect(() => {
    fetchSubscriptions()
  }, [user, enqueueSnackbar, fetchSubscriptions, pageRefreshCounter])

  if (!user || loading) {
    return (
      <Box sx={classes.subscriptionViewSection}>
        <Stack spacing={2}>
          <Skeleton width="400px" />
          <Skeleton width="400px" />
        </Stack>
      </Box>
    )
  }

  if (!isEditing && subscriptions.length === 0) {
    return (
      <Box sx={classes.subscriptionViewSection}>
        <ButtonLink
          startIcon={<AddOutlined />}
          onClick={() => {
            setIsEditing(true)
          }}
        >
          Add Subscription
        </ButtonLink>
      </Box>
    )
  }

  return (
    <Box sx={classes.subscriptionViewSection}>
      {!isEditing ? (
        <>
          {userDisabledPreferences.length > 0 && <Alert severity="warning">User has disabled {userDisabledPreferences.join('/')}</Alert>}
          <Stack
            spacing={4}
            sx={{
              m: 2,
            }}
          >
            {subscriptions?.map((us) => (
              <Grid
                key={us.id}
                container={true}
                sx={{
                  justifyContent: 'space-between',
                }}
              >
                <Typography variant="body1" sx={{ fontStyle: us.disabled ? 'italic' : undefined, color: us.disabled ? 'text.secondary' : 'text.primary' }}>
                  {us.name}
                </Typography>
                {us.disabled && <WarningAmberOutlined sx={{ color: 'warning.main' }} />}
              </Grid>
            ))}
          </Stack>
        </>
      ) : (
        <TransferList user={user} availableList={availableSchemas} activeList={subscriptions} onSave={saveSubscriptions} onCancel={() => setIsEditing(false)} />
      )}
    </Box>
  )
}

export default SubscriptionsContent
